<template>
    <div
        class="tw:relative"
        :class="[
            isExpanded ? 'expanded' : '',
            props.naked
                ? ''
                : props.flat
                    ? 'tw:bg-transparent tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200'
                    : 'tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-200',
            props.customClass
        ]"
        :data-cy="props.dataCy"
    >
        <div v-if="props.title || $slots['header-action'] || $scopedSlots['header-action']" class="tw:flex tw:justify-between tw:items-end tw:px-5 tw:py-4 tw:border-b tw:border-gray-100">
            <template v-if="props.skeleton">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <div class="tw:h-4 tw:w-28 tw:bg-gray-200 tw:rounded"></div>
                    <div class="tw:h-3 tw:w-44 tw:bg-gray-200 tw:rounded"></div>
                </div>
            </template>
            <template v-else>
                <div>
                    <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">{{ props.title }}</h2>
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">{{ props.subtitle }}</p>
                </div>
            </template>
            <slot name="header-action"></slot>
        </div>
        <div
            class="tw:w-full tw:overflow-x-auto"
            :style="bodyStyle"
        >
            <table class="tw:w-full tw:text-sm tw:border-collapse" :class="props.tableClass" style="table-layout: fixed;">
                <colgroup>
                    <col v-for="col in props.columns" :key="col.key" :class="col.class" :style="colHasWidth(col) ? '' : `width: ${props.defaultColWidth}`" />
                </colgroup>
                <!-- Skeleton mode -->
                <template v-if="props.skeleton">
                    <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider tw:animate-pulse">
                        <tr class="tw:border-b tw:border-gray-100">
                            <th v-for="col in props.columns" :key="col.key" :class="[headPad, 'tw:font-semibold', col.centered ? 'tw:text-center' : 'tw:text-left', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`skeleton-header-${col.key}`" :column="col">
                                        <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
                                    </slot>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="tw:animate-pulse">
                        <tr v-for="n in skeletonRowCount" :key="'skeleton-' + n" class="tw:border-b tw:border-gray-100">
                            <td v-for="col in props.columns" :key="col.key" :class="[cellPad, col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    <slot :name="`skeleton-${col.key}`" :column="col">
                                        <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-3/4"></div>
                                    </slot>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </template>

                <!-- Normal mode -->
                <template v-else>
                    <thead class="tw:text-gray-400 tw:text-xs tw:uppercase tw:tracking-wider">
                        <tr class="tw:border-b tw:border-gray-100">
                            <th v-for="col in props.columns" :key="col.key" :class="[headPad, 'tw:font-semibold', col.centered ? 'tw:text-center' : 'tw:text-left', col.class]">
                                <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                    {{ col.label }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody class="tw:divide-y tw:divide-gray-100">
                        <template v-for="(row, index) in props.data">
                            <tr
                                :key="row[props.rowKey]"
                                class="hover:tw:bg-gray-50 tw:transition-colors"
                                :class="props.clickableRows ? 'tw:cursor-pointer' : ''"
                                @click="onRowClick(row, index)"
                            >
                                <td v-for="col in props.columns" :key="col.key" :class="[cellPad, 'tw:overflow-hidden', col.truncate ? 'tw:truncate' : '', col.class]">
                                    <div :class="col.centered ? 'tw:flex tw:justify-center' : ''">
                                        <slot
                                            :name="`cell-${col.key}`"
                                            :row="row"
                                            :index="index"
                                            :value="getValue(row, col.field || col.key)"
                                        >
                                            <span v-if="col.truncate" :title="getValue(row, col.field || col.key)">{{ getValue(row, col.field || col.key) }}</span>
                                            <template v-else>{{ getValue(row, col.field || col.key) }}</template>
                                        </slot>
                                    </div>
                                </td>
                            </tr>
                            <!--
                                Fila expandida (opt-in): se pinta sólo si el
                                consumidor declara el slot `row-expanded` y la
                                fila está en `expandedRowKeys`. Ocupa el ancho
                                completo para poder colgar dentro un panel con
                                jerarquía propia (p. ej. sesiones → desglose).
                            -->
                            <tr
                                v-if="($slots['row-expanded'] || $scopedSlots['row-expanded']) && isRowExpanded(row)"
                                :key="`expanded-${row[props.rowKey]}`"
                                :data-cy="`row-expanded-${row[props.rowKey]}`"
                            >
                                <td :colspan="props.columns.length" class="tw:p-0">
                                    <slot name="row-expanded" :row="row" :index="index"></slot>
                                </td>
                            </tr>
                        </template>
                        <tr v-if="!props.loading && props.data.length === 0">
                            <td :colspan="props.columns.length" class="tw:px-5 tw:py-10 tw:text-center tw:text-gray-400">
                                {{ props.emptyText }}
                            </td>
                        </tr>
                    </tbody>
                    <!--
                        Pie de tabla (opt-in): fila de totales alineada con las
                        columnas, dentro del mismo `table-layout: fixed`.
                    -->
                    <tfoot v-if="$slots.footer || $scopedSlots.footer" class="tw:text-sm">
                        <slot name="footer" :data="props.data"></slot>
                    </tfoot>
                </template>
            </table>
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue'

    const props = defineProps({
        title: String,
        subtitle: String,
        columns: {
            type: Array,
            required: true
        },
        data: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        skeleton: {
            type: Boolean,
            default: false
        },
        skeletonRows: {
            type: Number,
            default: 5
        },
        rowKey: {
            type: String,
            default: 'id'
        },
        emptyText: {
            type: String,
            default: 'No hay resultados'
        },
        clickableRows: {
            type: Boolean,
            default: false
        },
        customClass: {
            type: String,
            default: ''
        },
        dataCy: {
            type: String,
            default: ''
        },
        flat: {
            type: Boolean,
            default: false
        },
        naked: {
            type: Boolean,
            default: false
        },
        defaultColWidth: {
            type: String,
            default: '100px'
        },
        maxBodyHeight: {
            type: String,
            default: ''
        },
        /**
         * Clases extra para el `<table>` (no para la card que lo envuelve). Sirve
         * para tablas que necesitan un ancho mínimo: con `table-layout: fixed` y
         * `w-full`, si la suma de las columnas fijas supera el ancho disponible,
         * la columna sin ancho (`w-auto`) colapsa en vez de dejar que el
         * contenedor scrollee. Un `min-width` le da suelo y el scroll horizontal
         * del wrapper hace el resto. Opt-in: sin la prop nada cambia.
         */
        tableClass: {
            type: String,
            default: ''
        },
        /**
         * Celdas compactas (menos padding horizontal). Opt-in: en tablas con
         * muchas columnas el padding por defecto se come el ancho disponible y
         * deja los textos truncados. Sin la prop nada cambia.
         */
        dense: {
            type: Boolean,
            default: false
        },
        /**
         * Claves (`row[rowKey]`) de las filas que tienen que mostrar su fila
         * expandida. Sólo aplica si el consumidor declara el slot
         * `row-expanded`. La comparación es tolerante a string/number: los ids
         * que vienen de la URL o de un select llegan como string.
         */
        expandedRowKeys: {
            type: Array,
            default: () => []
        }
    })

    const emit = defineEmits(['row-click'])

    /**
     * `onRowClick` is exposed as a named function so Vue can resolve the
     * `@click` handler at template-compile time. Using the inline
     * `emit('row-click', ...)` statement used to compile to an
     * `undefined` return value and produced a Vue warn in the parent
     * ("Invalid handler for event 'click': got undefined").
     */
    function onRowClick(row, index) {
        emit('row-click', { row, index })
    }

    const colHasWidth = col => /\btw:w-/.test(col.class || '')

    const headPad = computed(() => (props.dense ? 'tw:px-3 tw:py-2.5' : 'tw:px-5 tw:py-3'))
    const cellPad = computed(() => (props.dense ? 'tw:px-3 tw:py-2.5' : 'tw:px-5 tw:py-4'))

    /**
     * @param {Object} row
     * @returns {boolean} `true` si la fila debe mostrar su fila expandida.
     */
    function isRowExpanded(row) {
        const clave = row && row[props.rowKey]

        return props.expandedRowKeys.some(k => String(k) === String(clave))
    }

    const bodyStyle = computed(() => {
        if (!props.maxBodyHeight) return {}
        return {
            maxHeight: props.maxBodyHeight,
            overflowY: 'auto'
        }
    })

    const skeletonRowCount = computed(() => {
        if (props.skeletonRows > 0) return props.skeletonRows
        if (props.data.length > 0) return props.data.length
        return 5
    })

    const getValue = (row, field) => {
        if (!field) return ''

        return field.split('.').reduce((obj, key) => obj?.[key], row)
    }

    const isExpanded = computed(() => props.data.length >= 1 && props.data.length <= 3)
</script>
